import clsx from 'clsx'
import * as React from 'react'
import { Logger } from 'src/3rds/cube'
import { classToFComponent, FCClassContext } from 'src/3rds/cube-react'

import { makeStyles } from 'tss-react/mui'

import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import LinkIcon from '@mui/icons-material/Link'
import SearchIcon from '@mui/icons-material/Search'

import { formatCNPJ } from 'src/modules/cadastro-lote/CadastroLote.service'
import { descreverEvento, motivoSuficiente, oQueMuda } from '../GrupoManual.presenter'
import {
    Empresa,
    EmpresaDoGrupo,
    Evento,
    GRUPOS_POR_PAGINA,
    GrupoManualScope,
    MOTIVO_MINIMO,
    Negocio,
    OrdemGrupos,
    Sugestao
} from '../GrupoManual.scopes'

const LOG = Logger.get('GrupoManual-VIEW')

const ROTULO_FASE: Record<string, string> = { A: 'Abordagem', R: 'Reserva', K: 'Cota', C: 'Cliente' }
const COR_FASE: Record<string, 'info' | 'warning' | 'success' | 'default'> = {
    A: 'info',
    R: 'warning',
    K: 'warning',
    C: 'success'
}

const ROTULO_ORIGEM: Record<EmpresaDoGrupo['origem'], string> = {
    consultada: 'Empresa consultada',
    manual: 'Cadastro manual',
    socios: 'Pelos sócios'
}

const ROTULO_ORDEM: Record<OrdemGrupos, string> = {
    alterado: 'Alterado por último',
    grupo: 'Número do grupo',
    empresas: 'Mais empresas',
    negocios: 'Mais negócios em andamento'
}

const formatarQuando = (iso: string) =>
    new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })

const useStyles = makeStyles()((theme) => ({
    colunas: {
        display: 'flex',
        gap: theme.spacing(2.5),
        alignItems: 'flex-start',
        flexWrap: 'wrap'
    },
    colunaGrupo: {
        flex: '999 1 640px',
        minWidth: 0
    },
    colunaHistorico: {
        flex: '1 1 300px',
        minWidth: 0
    },
    view: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: theme.spacing(3),
        rowGap: theme.spacing(2.5),
        // O body do portal é fixo em 100vh, então o módulo precisa rolar por conta própria.
        overflow: 'auto',
        minHeight: 0,
        // Sem isto os cards encolhem até caber na coluna e recortam o próprio conteúdo (o Card do MUI
        // tem overflow: hidden).
        '& > *': {
            flexShrink: 0
        }
    },
    cabecalho: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap'
    },
    card: {
        borderRadius: 14,
        boxShadow: '0px 2px 12px rgba(0,0,0,0.05)'
    },
    busca: {
        maxWidth: 560
    },
    empresaCard: {
        display: 'flex',
        gap: theme.spacing(3),
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap'
    },
    acoes: {
        display: 'flex',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap'
    },
    cnpj: {
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 13,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap'
    },
    chips: {
        display: 'flex',
        gap: theme.spacing(0.5),
        flexWrap: 'wrap'
    },
    filtros: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    linhaClicavel: {
        cursor: 'pointer'
    },
    conferencia: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(2),
        borderRadius: 10,
        border: `1px solid ${theme.palette.divider}`
    },
    oQueMuda: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        borderRadius: 10,
        backgroundColor: theme.palette.action.hover
    }
}))

export type GrupoManualViewProps = {
    className?: string
    scope: GrupoManualScope
}

class GrupoManualClass implements FCClassContext<GrupoManualViewProps> {
    // :: Fields

    scope!: GrupoManualScope
    styles!: ReturnType<typeof useStyles>

    // :: emissors

    readonly emitAbaChange = (_evt: unknown, aba: 'empresa' | 'grupos') => this.scope.onAbaChange(aba).catch(LOG.caught)

    readonly emitFiltroChange = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onFiltroChange(evt.target.value).catch(LOG.caught)
    readonly emitOrdemChange = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onOrdemChange(evt.target.value as OrdemGrupos).catch(LOG.caught)
    readonly emitPaginaChange = (_evt: unknown, pagina: number) => this.scope.onPaginaChange(pagina).catch(LOG.caught)
    readonly emitAbrirEmpresa = (cnpj?: string) => () => {
        if (cnpj) this.scope.onAbrirEmpresa(cnpj).catch(LOG.caught)
    }

    readonly emitAbrirLigar = () => this.scope.onAbrirLigar().catch(LOG.caught)
    readonly emitFecharLigar = () => this.scope.onFecharLigar().catch(LOG.caught)
    readonly emitConfirmarLigar = () => this.scope.onConfirmarLigar().catch(LOG.caught)

    readonly emitAbrirRetirar = () => this.scope.onAbrirRetirar().catch(LOG.caught)
    readonly emitFecharRetirar = () => this.scope.onFecharRetirar().catch(LOG.caught)
    readonly emitConfirmarRetirar = () => this.scope.onConfirmarRetirar().catch(LOG.caught)
    readonly emitBuscaInput = (texto: string) => this.scope.onBuscaChange(texto).catch(LOG.caught)
    readonly emitEscolherEmpresa = (cnpj: string) => this.scope.onEscolherEmpresa(cnpj).catch(LOG.caught)
    readonly emitCnpjLigarInput = (texto: string) => this.scope.onCnpjLigarChange(texto).catch(LOG.caught)
    readonly emitEscolherOutra = (cnpj: string) => this.scope.onEscolherOutra(cnpj).catch(LOG.caught)
    readonly emitMotivoLigarChange = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onMotivoLigarChange(evt.target.value).catch(LOG.caught)
    readonly emitMotivoRetirarChange = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onMotivoRetirarChange(evt.target.value).catch(LOG.caught)

    // :: Methods

    onSyncState({ scope }: GrupoManualViewProps) {
        this.scope = scope
        this.styles = useStyles()
    }

    render({ className }: GrupoManualViewProps) {
        const { scope } = this
        const { classes } = this.styles

        return (
            <div className={clsx(classes.view, className)}>
                <Box className={classes.cabecalho}>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Grupos econômicos
                        </Typography>
                        <Typography color="text.secondary" fontSize={15}>
                            Veja como a LIA agrupa uma empresa e ligue empresas que ela não liga sozinha.
                        </Typography>
                    </Box>
                    <Tabs value={scope.aba} onChange={this.emitAbaChange} aria-label="Modo">
                        <Tab value="empresa" label="Por empresa" />
                        <Tab value="grupos" label="Todos os grupos manuais" />
                    </Tabs>
                </Box>

                {scope.aba === 'empresa' ? this.renderPorEmpresa() : this.renderTodosOsGrupos()}

                {this.renderDialogoLigar()}
                {this.renderDialogoRetirar()}
            </div>
        )
    }

    // :: Aba "por empresa"

    renderPorEmpresa() {
        const { scope } = this
        const { classes } = this.styles

        return (
            <>
                <Card className={classes.card}>
                    <CardContent>
                        <Box className={classes.busca}>
                            {this.renderBuscaEmpresa({
                                label: 'Empresa',
                                valor: scope.busca,
                                sugestoes: scope.sugestoes,
                                onInput: this.emitBuscaInput,
                                onEscolher: this.emitEscolherEmpresa,
                                erro: scope.erroBusca,
                                ajuda: 'Digite o CNPJ, ou parte do nome de uma empresa que a AG tem no HubSpot.',
                                carregando: scope.carregandoSituacao
                            })}
                        </Box>
                    </CardContent>
                </Card>

                {scope.situacao ? (
                    <>
                        {this.renderEmpresa()}
                        <Box className={classes.colunas}>
                            <Box className={classes.colunaGrupo}>{this.renderGrupoLia()}</Box>
                            {scope.situacao.grupoManual && (
                                <Box className={classes.colunaHistorico}>{this.renderHistorico()}</Box>
                            )}
                        </Box>
                    </>
                ) : (
                    !scope.carregandoSituacao && (
                        <Typography color="text.secondary">
                            Digite o CNPJ de uma empresa para ver o grupo que a LIA usa nas decisões e ligá-la a outra
                            empresa. Para revisar os grupos cadastrados, use a aba “Todos os grupos manuais”.
                        </Typography>
                    )
                )}
            </>
        )
    }

    renderEmpresa() {
        const { scope } = this
        const { classes } = this.styles
        const situacao = scope.situacao
        if (!situacao) return null
        const { empresa, grupoManual } = situacao
        const filial = empresa.cnpjInformado !== empresa.cnpjMatriz

        return (
            <Card className={classes.card}>
                <CardContent className={classes.empresaCard}>
                    <Box display="flex" flexDirection="column" gap={0.5} minWidth={0}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="h6" fontWeight={700}>
                                {empresa.razaoSocial ?? 'Razão social não informada'}
                            </Typography>
                            <Chip
                                size="small"
                                color={empresa.ativa ? 'success' : 'default'}
                                label={empresa.ativa ? 'Ativa' : 'Inativa'}
                            />
                        </Box>
                        <span className={classes.cnpj}>
                            {formatCNPJ(empresa.cnpjMatriz)} · matriz
                            {filial ? ` (você digitou a filial ${formatCNPJ(empresa.cnpjInformado)})` : ''}
                        </span>
                        <Typography fontSize={15} mt={1}>
                            {grupoManual ? (
                                <>
                                    Está no grupo manual <b>{grupoManual.empresaPrincipal ?? grupoManual.grupoId}</b>{' '}
                                    (nº {grupoManual.grupoId}), com {grupoManual.qtdeEmpresas} empresas cadastradas à
                                    mão.
                                </>
                            ) : (
                                'Não está em nenhum grupo manual: a LIA a agrupa só pelos sócios.'
                            )}
                        </Typography>
                    </Box>
                    <Box className={classes.acoes}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<LinkIcon />}
                            onClick={this.emitAbrirLigar}
                            disabled={scope.salvando}
                        >
                            Ligar a outra empresa
                        </Button>
                        {grupoManual && (
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={this.emitAbrirRetirar}
                                disabled={scope.salvando}
                            >
                                Tirar do grupo manual
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>
        )
    }

    renderGrupoLia() {
        const { scope } = this
        const { classes } = this.styles
        const grupo = scope.situacao?.grupoLia ?? []

        return (
            <Card className={classes.card}>
                {scope.carregandoSituacao && <LinearProgress />}
                <CardContent>
                    <Box display="flex" alignItems="baseline" justifyContent="space-between" gap={2} flexWrap="wrap">
                        <Typography variant="h6" fontWeight={600}>
                            Grupo que a LIA usa hoje · {grupo.length} {grupo.length === 1 ? 'empresa' : 'empresas'}
                        </Typography>
                        <Typography fontSize={13} color="text.secondary">
                            Decide bloqueios, o prazo de 45 dias e “grupo já é cliente”
                        </Typography>
                    </Box>
                    <TableContainer sx={{ mt: 1.5 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Empresa</TableCell>
                                    <TableCell>Por que está no grupo</TableCell>
                                    <TableCell>Negócios</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {grupo.map((item) => (
                                    <TableRow key={item.cnpj} hover>
                                        <TableCell>
                                            <Typography fontWeight={600} fontSize={14}>
                                                {item.razaoSocial ?? '—'}
                                                {!item.ativa && <Chip size="small" label="Inativa" sx={{ ml: 1 }} />}
                                            </Typography>
                                            <span className={classes.cnpj}>{formatCNPJ(item.cnpj)}</span>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                variant={item.origem === 'consultada' ? 'filled' : 'outlined'}
                                                color={item.origem === 'manual' ? 'secondary' : 'default'}
                                                label={ROTULO_ORIGEM[item.origem]}
                                            />
                                        </TableCell>
                                        <TableCell>{this.renderNegocios(item.negocios)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        )
    }

    renderNegocios(negocios: Negocio[]) {
        const { classes } = this.styles
        if (negocios.length === 0) {
            return <Typography color="text.secondary">—</Typography>
        }
        return (
            <Box className={classes.chips}>
                {negocios.map((negocio) => (
                    <Chip
                        key={negocio.dealId}
                        size="small"
                        variant="outlined"
                        color={COR_FASE[negocio.fase] ?? 'default'}
                        label={`${ROTULO_FASE[negocio.fase] ?? negocio.fase}${
                            negocio.parceiro ? ` · ${negocio.parceiro}` : ''
                        }`}
                    />
                ))}
            </Box>
        )
    }

    // :: Aba "todos os grupos"

    renderTodosOsGrupos() {
        const { scope } = this
        const { classes } = this.styles

        return (
            <>
                <Box className={classes.filtros}>
                    <TextField
                        size="small"
                        sx={{ flex: '1 1 360px', maxWidth: 560 }}
                        placeholder="Filtrar por CNPJ ou razão social de qualquer empresa do grupo"
                        value={scope.filtro}
                        onChange={this.emitFiltroChange}
                        inputProps={{ 'aria-label': 'Filtrar grupos' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Ordenar por"
                        value={scope.ordem}
                        onChange={this.emitOrdemChange}
                        sx={{ minWidth: 240 }}
                    >
                        {(Object.keys(ROTULO_ORDEM) as OrdemGrupos[]).map((ordem) => (
                            <MenuItem key={ordem} value={ordem}>
                                {ROTULO_ORDEM[ordem]}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                <Card className={classes.card}>
                    {scope.carregandoLista && <LinearProgress />}
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Grupo</TableCell>
                                    <TableCell align="right">Empresas</TableCell>
                                    <TableCell>Negócios em andamento</TableCell>
                                    <TableCell>Última alteração</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scope.grupos.map((grupo) => (
                                    <TableRow
                                        key={grupo.grupoId}
                                        hover
                                        className={classes.linhaClicavel}
                                        onClick={this.emitAbrirEmpresa(grupo.cnpjPrincipal)}
                                    >
                                        <TableCell>
                                            <Typography fontWeight={600} fontSize={14} color="primary">
                                                {grupo.empresaPrincipal ?? `Grupo ${grupo.grupoId}`}
                                            </Typography>
                                            <Typography fontSize={12} color="text.secondary">
                                                nº {grupo.grupoId}
                                                {grupo.qtdeEmpresas > 1
                                                    ? ` · e mais ${grupo.qtdeEmpresas - 1} empresas`
                                                    : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">{grupo.qtdeEmpresas}</TableCell>
                                        <TableCell>
                                            {grupo.negociosEmAndamento > 0 ? (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    label={`${grupo.negociosEmAndamento} em andamento`}
                                                />
                                            ) : (
                                                <Typography color="text.secondary">—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {grupo.ultimaAlteracao ? (
                                                <>
                                                    <Typography fontSize={14}>
                                                        {descreverEvento(grupo.ultimaAlteracao)}
                                                    </Typography>
                                                    <Typography fontSize={12} color="text.secondary">
                                                        {formatarQuando(grupo.ultimaAlteracao.quando)}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <Typography fontSize={13} color="text.secondary">
                                                    Sem registro (anterior ao histórico)
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!scope.carregandoLista && scope.grupos.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4}>
                                            <Typography color="text.secondary" fontSize={14}>
                                                {scope.filtro.trim()
                                                    ? 'Nenhum grupo com essa empresa.'
                                                    : 'Nenhum grupo manual cadastrado.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={scope.total}
                        page={scope.pagina}
                        rowsPerPage={GRUPOS_POR_PAGINA}
                        rowsPerPageOptions={[GRUPOS_POR_PAGINA]}
                        onPageChange={this.emitPaginaChange}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                    />
                </Card>
            </>
        )
    }

    renderHistorico() {
        const { scope } = this
        const { classes } = this.styles
        const historico = scope.situacao?.historico ?? []
        const temCriacao = historico.some((evento) => evento.tipo === 'criado')

        return (
            <Card className={classes.card}>
                <CardContent>
                    <Typography variant="h6" fontWeight={600} mb={1.5}>
                        Histórico do grupo manual
                    </Typography>
                    <Box
                        component="ol"
                        m={0}
                        p={0}
                        sx={{ listStyle: 'none' }}
                        display="flex"
                        flexDirection="column"
                        gap={2}
                    >
                        {historico.map((evento) => this.renderEvento(evento))}
                        {!temCriacao && (
                            <li>
                                <Typography fontSize={13} color="text.secondary">
                                    O grupo é anterior ao histórico (14/10/2026): não se sabe quem o criou nem quando.
                                </Typography>
                            </li>
                        )}
                    </Box>
                </CardContent>
            </Card>
        )
    }

    renderEvento(evento: Evento) {
        return (
            <li key={evento.id}>
                <Typography fontSize={14}>{descreverEvento(evento)}</Typography>
                <Typography fontSize={13} color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    “{evento.motivo}”
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                    {formatarQuando(evento.quando)}
                </Typography>
            </li>
        )
    }

    // :: Busca de empresa por CNPJ ou nome

    renderBuscaEmpresa(args: {
        label: string
        valor: string
        sugestoes: Sugestao[]
        onInput: (texto: string) => void
        onEscolher: (cnpj: string) => void
        erro?: string
        ajuda: string
        carregando?: boolean
        autoFocus?: boolean
        desabilitado?: boolean
    }) {
        return (
            <Autocomplete<Sugestao, false, false, true>
                freeSolo
                options={args.sugestoes}
                inputValue={args.valor}
                // As sugestões já vêm filtradas pelo servidor.
                filterOptions={(opcoes) => opcoes}
                getOptionLabel={(opcao) => (typeof opcao === 'string' ? opcao : opcao.razaoSocial ?? opcao.cnpj)}
                onInputChange={(_evt, texto, motivo) => {
                    // 'reset' vem do próprio MUI ao escolher uma opção; só interessa o que o operador digita.
                    if (motivo === 'input' || motivo === 'clear') args.onInput(texto)
                }}
                onChange={(_evt, opcao) => {
                    if (opcao && typeof opcao !== 'string') args.onEscolher(opcao.cnpj)
                }}
                disabled={args.desabilitado}
                renderOption={(props, opcao) => (
                    <li {...props} key={opcao.cnpj}>
                        <Box display="flex" flexDirection="column">
                            <Typography fontSize={14} fontWeight={600}>
                                {opcao.razaoSocial ?? '—'}
                            </Typography>
                            <Typography fontSize={12} color="text.secondary">
                                {formatCNPJ(opcao.cnpj)}
                                {opcao.grupoAtual != null ? ` · grupo manual ${opcao.grupoAtual}` : ''}
                            </Typography>
                        </Box>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        autoFocus={args.autoFocus}
                        label={args.label}
                        placeholder="CNPJ ou nome"
                        error={!!args.erro}
                        helperText={args.erro ?? args.ajuda}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: args.carregando ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={18} />
                                </InputAdornment>
                            ) : (
                                params.InputProps.endAdornment
                            )
                        }}
                    />
                )}
            />
        )
    }

    // :: Diálogos

    renderMotivo(valor: string, onChange: (evt: React.ChangeEvent<HTMLInputElement>) => void) {
        const curto = valor.trim().length > 0 && !motivoSuficiente(valor)
        return (
            <TextField
                fullWidth
                multiline
                minRows={2}
                margin="normal"
                label="Motivo"
                placeholder="Por que estas empresas são (ou não são) do mesmo grupo?"
                value={valor}
                onChange={onChange}
                error={curto}
                helperText={
                    curto
                        ? `Escreva pelo menos ${MOTIVO_MINIMO} caracteres.`
                        : 'Fica no histórico do grupo, com seu nome.'
                }
                disabled={this.scope.salvando}
            />
        )
    }

    renderConferencia(empresa: Empresa | undefined) {
        const { classes } = this.styles
        if (this.scope.conferindo) {
            return (
                <Box className={classes.conferencia} display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} /> Conferindo o CNPJ…
                </Box>
            )
        }
        if (!empresa) return null

        return (
            <Box className={classes.conferencia}>
                <Typography fontWeight={600}>{empresa.razaoSocial ?? 'Razão social não informada'}</Typography>
                <span className={classes.cnpj}>
                    {formatCNPJ(empresa.cnpjMatriz)} · {empresa.ativa ? 'ativa' : 'inativa'}
                </span>
                {empresa.cnpjInformado !== empresa.cnpjMatriz && (
                    <Typography fontSize={13} color="text.secondary">
                        Você digitou uma filial; o grupo guarda a matriz.
                    </Typography>
                )}
            </Box>
        )
    }

    renderDialogoLigar() {
        const { scope } = this
        const { classes } = this.styles
        const situacao = scope.situacao
        const outra = scope.conferenciaLigar
        const efeito = situacao && outra ? oQueMuda(situacao, outra, scope.simulacao) : undefined
        const unir = efeito?.modo === 'unir'
        const podeLigar =
            !!efeito &&
            !efeito.impedimento &&
            !scope.conferindo &&
            !scope.simulando &&
            !!scope.simulacao &&
            motivoSuficiente(scope.motivoLigar)

        return (
            <Dialog open={scope.ligarAberto} onClose={this.emitFecharLigar} maxWidth="sm" fullWidth>
                <DialogTitle>Ligar {situacao?.empresa.razaoSocial ?? 'a empresa'} a outra empresa</DialogTitle>
                <DialogContent>
                    <DialogContentText>As duas passam a ser um só grupo econômico para a LIA.</DialogContentText>
                    <Box mt={2} mb={1}>
                        {this.renderBuscaEmpresa({
                            label: 'Outra empresa',
                            valor: scope.cnpjLigar,
                            sugestoes: scope.sugestoesLigar,
                            onInput: this.emitCnpjLigarInput,
                            onEscolher: this.emitEscolherOutra,
                            erro: scope.erroLigar,
                            ajuda: 'CNPJ ou parte do nome.',
                            carregando: scope.conferindo,
                            autoFocus: true,
                            desabilitado: scope.salvando
                        })}
                    </Box>
                    {this.renderConferencia(outra)}
                    {efeito && !efeito.impedimento && (
                        <Box className={classes.oQueMuda}>
                            <Typography fontWeight={600} mb={1}>
                                O que muda
                            </Typography>
                            <Box component="ul" m={0} pl={2.5} display="flex" flexDirection="column" gap={0.75}>
                                {efeito.frases.map((frase) => (
                                    <li key={frase.texto}>
                                        <Typography
                                            fontSize={14}
                                            fontWeight={frase.alerta ? 600 : 400}
                                            color={frase.alerta ? 'warning.dark' : 'text.primary'}
                                        >
                                            {frase.texto}
                                        </Typography>
                                    </li>
                                ))}
                            </Box>
                            {scope.simulando && (
                                <Box display="flex" alignItems="center" gap={1} mt={1}>
                                    <CircularProgress size={14} />
                                    <Typography fontSize={13} color="text.secondary">
                                        Conferindo os negócios afetados…
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                    {efeito && !efeito.impedimento && this.renderMotivo(scope.motivoLigar, this.emitMotivoLigarChange)}
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.emitFecharLigar} disabled={scope.salvando}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color={unir ? 'warning' : 'primary'}
                        onClick={this.emitConfirmarLigar}
                        disabled={!podeLigar || scope.salvando}
                    >
                        {unir ? 'Unir os dois grupos' : 'Ligar as empresas'}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    renderDialogoRetirar() {
        const { scope } = this
        const situacao = scope.situacao
        const grupo = situacao?.grupoManual
        const ultima = (grupo?.qtdeEmpresas ?? 0) <= 2

        return (
            <Dialog open={scope.retirarAberto} onClose={this.emitFecharRetirar} maxWidth="sm" fullWidth>
                <DialogTitle>Tirar do grupo manual</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tirar <b>{situacao?.empresa.razaoSocial ?? formatCNPJ(situacao?.empresa.cnpjMatriz ?? '')}</b>{' '}
                        do grupo manual nº {grupo?.grupoId}?
                    </Typography>
                    {ultima && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            O grupo fica com uma empresa só, que continua cadastrada nele.
                        </Alert>
                    )}
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        A empresa continua no grupo das outras se a LIA a ligar pelos sócios.
                    </Alert>
                    {this.renderMotivo(scope.motivoRetirar, this.emitMotivoRetirarChange)}
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.emitFecharRetirar} disabled={scope.salvando}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={this.emitConfirmarRetirar}
                        disabled={scope.salvando || !motivoSuficiente(scope.motivoRetirar)}
                    >
                        Tirar do grupo
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }
}

export const GrupoManualView = classToFComponent(GrupoManualClass, React)
export default GrupoManualView
